import { memo, useCallback, useContext } from 'react';
import { T, useT } from '../../../translation/src';
import { DateFormats, Language, MeasurementUnits, useUserChangeDateFormatMutation, useUserChangeLanguageMutation } from '../../../types/graphqlTypes';
import { ButtonToggle } from '../../../components/ButtonToggle';
import { FormInput } from '../../../components/formik/FormInput';
import useOpen from '../../../hooks/useOpen';
import { UserContext } from '../../../contexts/UserContext';
import { CURRENT_USER } from '../../../graphql/user';
import { ResetPasswordModal } from '../ResetPasswordModal';
import { Modal } from '../../../components/Modal';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { FormSelect, FormSelectProps } from '../../../components/formik/FormSelect';
import { Option } from '../../../components/inputs/Select';
import { useLanguageOptions } from '../../../hooks/useLanguageOptions';
import { UserColorPreference } from './UserColorPreference';
import { NavigationMode, useDarkModePreference, useHideDebugButton, useNavigationModePreference, useShowDebugUi, useUnitPreference } from '../../../modules/Properties/integration/UserSettingsIntegration';
import { CheckBox } from '../../../components/inputs/CheckBox';
import { LanguageContext } from '../../../translation/src/context/LanguageContext';

const isValidLanguage = (language: string): language is Language => Object.values(Language).some((l) => l === language);

export interface UserSettingsModalProps {
  onClose: () => void;
  open?: boolean;
}

const useTranslations = () => {
  const password = useT('password', { swc: true });
  const language = useT('language', { swc: true });
  return { password, language };
};

const unitButtonToggleOptions = [
  { value: MeasurementUnits.Meters, label: <T _str="Metric" swc /> },
  { value: MeasurementUnits.Feet, label: <T _str="Imperial" swc /> },
];

const shortDateFormatButtonToggleOptions = [
  { value: DateFormats.Ddmmyyyy, label: <T _str="D/M/Y" swc /> },
  { value: DateFormats.Mmddyyyy, label: <T _str="M/D/Y" swc /> },
  { value: DateFormats.Yyyymmdd, label: <T _str="Y/M/D" swc /> },
];

const darkModeToggleOptions = [
  { value: true, label: <T _str="Dark mode" swc /> },
  { value: false, label: <T _str="Light mode" swc /> },
];

// Touchpad (default): scroll pans (two-finger scroll) and pinch /
// ctrl+scroll zooms toward the cursor. Mouse: scroll wheel zooms.
const navigationModeToggleOptions = [
  { value: NavigationMode.Touchpad, label: <T _str="Touchpad" swc /> },
  { value: NavigationMode.Mouse, label: <T _str="Mouse" swc /> },
];

const UserSettingsModal_: React.FC2<UserSettingsModalProps> = ({ onClose, open }) => {
  const translations = useTranslations();
  const languageOptions = useLanguageOptions();
  const { onClose: closePasswordModal, onOpen: openPasswordModal, open: passwordModalOpen } = useOpen();
  const { dateFormat, language, id: userId, isSuperAdmin } = useContext(UserContext);
  const { hideDebugButton, setHideDebugButton } = useHideDebugButton();
  const { showDebugUi, setShowDebugUi } = useShowDebugUi();
  const { setLanguage } = useContext(LanguageContext);
  const { unit: measurementUnit, setUnit } = useUnitPreference();
  const { darkMode, setDarkMode } = useDarkModePreference();
  const { navigationMode, setNavigationMode } = useNavigationModePreference();
  const [changeUserDateFormat] = useUserChangeDateFormatMutation();
  const [changeLanguage] = useUserChangeLanguageMutation();

  const onChangeUnit = useCallback(
    (value: MeasurementUnits) => {
      setUnit(value);
    },
    [setUnit],
  );

  const onChangeDateFormat = useCallback(
    async (value: DateFormats) => {
      await changeUserDateFormat({
        variables: { inFormat: value },
        refetchQueries: [{ query: CURRENT_USER }],
        awaitRefetchQueries: true,
      });
    },
    [changeUserDateFormat],
  );

  // Writing the reactive property is the whole toggle: `useDarkModeTheme` at
  // the app root applies the DOM side effects (dark class, localStorage) and
  // every other subscriber (viewer plan view, tool settings panel) follows.
  const onChangeDarkMode = setDarkMode;

  const onChangeLanguage: FormSelectProps['onChange'] = useCallback(
    async (event) => {
      const language = event.target.value;
      if (!isValidLanguage(language)) return;
      setLanguage(language);
      await changeLanguage({
        variables: { language },
        refetchQueries: [{ query: CURRENT_USER }],
        awaitRefetchQueries: true,
        optimisticResponse: {
          userChangeLanguage: {
            __typename: 'User',
            id: userId,
            language,
          },
          __typename: 'Mutation',
        },
      });
    },
    [changeLanguage, setLanguage, userId],
  );

  return (
    <div className="overflow-auto">
      <Modal.Container onClose={onClose} open={open} panelClassName="w-3/4 max-w-md max-h-[90vh] overflow-y-auto">
        <Modal.Header>
          <div className="z-50 w-full max-w-full text-xl">
            <div className="flex justify-between">
              <T _str="settings" swc />
              <div className="flex space-x-1 ">
                <XMarkIcon onClick={onClose} className="w-6 h-6 hover:bg-neon-green-300 hover:dark:bg-black hover:dark:text-white" />
              </div>
            </div>
          </div>
        </Modal.Header>
        <div className="flex flex-col w-full pb-4 bg-white dark:bg-eerie-black dark:text-white border-gray-300 dark:border-gray-400 px-5 sm:px-10">
          <FormInput name="Password" disabled type="password" defaultValue="secretsecretsecret" label={translations.password} />
          <div className="mb-2 text-blue-600 cursor-pointer dark:text-neon-green-400" onClick={openPasswordModal}>
            <T _str="reset password" swc />
          </div>
          <FormSelect name="language" label={translations.language} placeholder={translations.language} value={language} onChange={onChangeLanguage}>
            {languageOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </FormSelect>
          <span className="mb-2 font-semibold">
            <T _str="measurement units" swc />
          </span>
          <div className="flex flex-col p-4 bg-white border border-gray-300 rounded dark:bg-eerie-black w-full">
            <ButtonToggle options={unitButtonToggleOptions} onChange={onChangeUnit} value={measurementUnit} />
          </div>
          <span className="mt-4 mb-2 font-semibold">
            <T _str="date format" swc />
          </span>
          <div className="flex flex-col p-4 bg-white border border-gray-300 rounded dark:bg-eerie-black w-full">
            <ButtonToggle options={shortDateFormatButtonToggleOptions} onChange={onChangeDateFormat} value={dateFormat} />
          </div>
          <span className="mt-4 mb-2 font-semibold">
            <T _str="navigation mode" swc />
          </span>
          <div className="flex flex-col p-4 bg-white border border-gray-300 rounded dark:bg-eerie-black w-full">
            <ButtonToggle options={navigationModeToggleOptions} onChange={setNavigationMode} value={navigationMode} />
            <span className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              <T _str="In 2D views the mouse scroll wheel zooms. On a touchpad, scrolling pans the view. Pinch or ctrl+scroll zooms" swc />
            </span>
          </div>
          <span className="mt-4 mb-2 font-semibold">
            <T _str="theme" swc />
          </span>
          <div className="flex flex-col p-4 bg-white border border-gray-300 rounded dark:bg-eerie-black w-full">
            <ButtonToggle options={darkModeToggleOptions} onChange={onChangeDarkMode} value={darkMode || false} />
          </div>
          <span className="mt-4 mb-2 font-semibold">
            <T _str="color preferences" swc />
          </span>
          <div className="flex flex-col p-4 bg-white border border-gray-300 rounded dark:bg-eerie-black w-full">
            <UserColorPreference />
          </div>
          {isSuperAdmin && (
            <>
              <span className="mt-4 mb-2 font-semibold">
                <T _str="debug" swc />
              </span>
              <div className="flex flex-col p-4 bg-white border border-gray-300 rounded dark:bg-eerie-black w-full">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <CheckBox checked={hideDebugButton} onChange={(e) => setHideDebugButton(e.target.checked)} />
                  <span>
                    <T _str="hide debug button" swc />
                  </span>
                </label>
                <label className="flex items-center mt-2 space-x-2 cursor-pointer">
                  <CheckBox checked={showDebugUi} onChange={(e) => setShowDebugUi(e.target.checked)} />
                  <span>
                    <T _str="show debug ui" swc />
                  </span>
                </label>
              </div>
            </>
          )}
        </div>
        <ResetPasswordModal onClose={closePasswordModal} open={passwordModalOpen} />
      </Modal.Container>
    </div>
  );
};

export const UserSettingModal = memo(UserSettingsModal_);
